/**
 * Old Mutual Branded Landing Page
 * 
 * Custom homepage for Old Mutual tenant featuring:
 * - Old Mutual branding (green theme)
 * - Featured courses, learning labs, pathways, masterclasses
 * - Campaigns section
 * - User dashboard link (for authenticated users)
 * - Enterprise SSO login
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { 
  BookOpen, 
  Users, 
  ArrowRight,
  GraduationCap,
  Building2,
  FlaskConical,
  Route,
  Play,
  Trophy,
  Target,
  Star,
  ChevronRight,
  Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
const API = `${API_BASE}/api`;

// Old Mutual Brand Colors
const OM_GREEN = '#00A94F';
const OM_DARK_GREEN = '#003D1F';

// Course Card Component
const CourseCard = ({ course, onClick }) => (
  <div 
    onClick={onClick}
    className="group cursor-pointer bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
  >
    <div className="aspect-video relative overflow-hidden">
      <img 
        src={course.thumbnail_url || '/placeholder-course.jpg'} 
        alt={course.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
      {course.is_featured && (
        <span className="absolute top-3 left-3 px-2 py-1 bg-[#00A94F] text-white text-xs font-medium rounded">
          Featured
        </span>
      )}
    </div>
    <div className="p-4">
      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-[#00A94F] transition-colors">
        {course.title}
      </h3>
      <p className="text-sm text-gray-500 line-clamp-2 mb-3">{course.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{course.duration || '2-3 hours'}</span>
        <ChevronRight className="h-4 w-4 text-[#00A94F] group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  </div>
);

// Section Header Component
const SectionHeader = ({ icon: Icon, title, subtitle, viewAllLink }) => (
  <div className="flex items-center justify-between mb-6">
    <div>
      <div className="flex items-center gap-3 mb-1">
        <div className="p-2 bg-[#00A94F]/10 rounded-lg">
          <Icon className="h-5 w-5 text-[#00A94F]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      </div>
      {subtitle && <p className="text-gray-500 ml-12">{subtitle}</p>}
    </div>
    {viewAllLink && (
      <Link 
        to={viewAllLink} 
        className="text-[#00A94F] hover:text-[#008A3F] font-medium flex items-center gap-1"
      >
        View All <ArrowRight className="h-4 w-4" />
      </Link>
    )}
  </div>
);

// Stats Card
const StatCard = ({ icon: Icon, value, label }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
    <Icon className="h-8 w-8 text-[#00A94F] mx-auto mb-3" />
    <div className="text-3xl font-bold text-gray-900 mb-1">{value}</div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);

export default function OldMutualLanding() {
  const { user, isAuthenticated } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [learningLabs, setLearningLabs] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [masterclasses, setMasterclasses] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ courses: 0, labs: 0, pathways: 0, learners: 0 });

  // Use tenant branding or defaults
  const logoUrl = tenant?.branding?.logoUrl || null;
  const primaryColor = tenant?.branding?.primaryColor || OM_GREEN;
  const companyName = tenant?.name || 'Old Mutual';

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [coursesRes, labsRes, pathwaysRes, masterclassesRes] = await Promise.all([
        axios.get(`${API}/courses?limit=6&status=published`).catch(() => ({ data: { courses: [] } })),
        axios.get(`${API}/learning-labs?limit=4&status=published`).catch(() => ({ data: { labs: [] } })),
        axios.get(`${API}/learning-pathways?limit=4&is_published=true`).catch(() => ({ data: { pathways: [] } })),
        axios.get(`${API}/masterclasses?limit=4&status=published`).catch(() => ({ data: { masterclasses: [] } }))
      ]);

      setCourses(coursesRes.data.courses || []);
      setLearningLabs(labsRes.data.labs || labsRes.data.learning_labs || []);
      setPathways(pathwaysRes.data.pathways || []);
      setMasterclasses(masterclassesRes.data.masterclasses || []);
      
      setStats({
        courses: coursesRes.data.total || coursesRes.data.courses?.length || 0,
        labs: labsRes.data.total || labsRes.data.labs?.length || 0,
        pathways: pathwaysRes.data.total || pathwaysRes.data.pathways?.length || 0,
        learners: 1000 // Placeholder
      });
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-10 w-auto" />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="h-10 w-10 bg-[#00A94F] rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">OM</span>
                  </div>
                  <span className="font-bold text-xl text-gray-900">{companyName}</span>
                </div>
              )}
              <span className="text-sm text-gray-400 border-l border-gray-300 pl-3 ml-2">Learning Academy</span>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/catalog" className="text-gray-600 hover:text-[#00A94F] font-medium">Courses</Link>
              <Link to="/learning-labs" className="text-gray-600 hover:text-[#00A94F] font-medium">Learning Labs</Link>
              <Link to="/learning-pathways" className="text-gray-600 hover:text-[#00A94F] font-medium">Pathways</Link>
              <Link to="/masterclasses" className="text-gray-600 hover:text-[#00A94F] font-medium">Masterclasses</Link>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Link 
                  to="/dashboard"
                  className="px-4 py-2 bg-[#00A94F] hover:bg-[#008A3F] text-white rounded-lg font-medium transition-colors"
                >
                  My Dashboard
                </Link>
              ) : (
                <>
                  <Link 
                    to="/login"
                    className="px-4 py-2 text-gray-700 hover:text-[#00A94F] font-medium transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/enterprise-sso"
                    className="px-4 py-2 bg-[#00A94F] hover:bg-[#008A3F] text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <Shield className="h-4 w-4" />
                    SSO Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#00A94F] to-[#003D1F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Welcome to {companyName} Learning Academy
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Empower your career with world-class courses, interactive learning labs, 
              and structured pathways designed for your professional growth.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/catalog"
                className="px-6 py-3 bg-white text-[#00A94F] hover:bg-green-50 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                Explore Courses <ArrowRight className="h-5 w-5" />
              </Link>
              {!isAuthenticated && (
                <Link 
                  to="/enterprise-sso"
                  className="px-6 py-3 border-2 border-white text-white hover:bg-white/10 rounded-lg font-semibold transition-colors"
                >
                  Sign In with Microsoft
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={BookOpen} value={stats.courses}  label="Courses" />
            <StatCard icon={FlaskConical} value={stats.labs} label="Learning Labs" />
            <StatCard icon={Route} value={stats.pathways} label="Pathways" />
            <StatCard icon={Users} value={`${stats.learners}+`} label="Learners" />
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader 
            icon={BookOpen} 
            title="Featured Courses" 
            subtitle="Start your learning journey with our top-rated courses"
            viewAllLink="/catalog"
          />
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => (
                <div key={i} className="bg-gray-100 rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : courses.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.slice(0, 6).map(course => (
                <CourseCard 
                  key={course.course_id} 
                  course={course}
                  onClick={() => navigate(`/course/${course.course_id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No courses available yet. Check back soon!
            </div>
          )}
        </div>
      </section>

      {/* Learning Labs */}
      {learningLabs.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader 
              icon={FlaskConical} 
              title="Learning Labs" 
              subtitle="Hands-on simulations and scenario-based training"
              viewAllLink="/learning-labs"
            />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {learningLabs.slice(0, 4).map(lab => (
                <div 
                  key={lab.lab_id}
                  onClick={() => navigate(`/learning-lab/${lab.lab_id}`)}
                  className="group cursor-pointer bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#00A94F]/30 transition-all"
                >
                  <div className="h-12 w-12 bg-[#00A94F]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#00A94F]/20 transition-colors">
                    <FlaskConical className="h-6 w-6 text-[#00A94F]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[#00A94F] transition-colors">
                    {lab.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2">{lab.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Learning Pathways */}
      {pathways.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader 
              icon={Route} 
              title="Learning Pathways" 
              subtitle="Structured journeys to advance your career"
              viewAllLink="/learning-pathways"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pathways.slice(0, 4).map(pathway => (
                <div 
                  key={pathway.pathway_id}
                  onClick={() => navigate(`/learning-pathway/${pathway.pathway_id}`)}
                  className="group cursor-pointer bg-gradient-to-r from-[#00A94F]/5 to-transparent rounded-xl p-6 border border-[#00A94F]/20 hover:border-[#00A94F]/40 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-14 w-14 bg-[#00A94F] rounded-xl flex items-center justify-center shrink-0">
                      <Route className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-2 group-hover:text-[#00A94F] transition-colors">
                        {pathway.title}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3">{pathway.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span>{pathway.courses?.length || 0} courses</span>
                        <span>{pathway.estimated_duration || '10-20 hours'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Masterclasses */}
      {masterclasses.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader 
              icon={Play} 
              title="Masterclasses" 
              subtitle="Expert-led video sessions with interactive quizzes"
              viewAllLink="/masterclasses"
            />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {masterclasses.slice(0, 4).map(mc => (
                <div 
                  key={mc.masterclass_id}
                  onClick={() => navigate(`/masterclass/${mc.masterclass_id}`)}
                  className="group cursor-pointer bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all"
                >
                  <div className="aspect-video relative bg-gray-200">
                    {mc.thumbnail_url ? (
                      <img src={mc.thumbnail_url} alt={mc.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#00A94F]/10">
                        <Play className="h-12 w-12 text-[#00A94F]" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="h-14 w-14 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="h-6 w-6 text-[#00A94F] ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-[#00A94F] transition-colors">
                      {mc.title}
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">{mc.instructor || 'Expert Instructor'}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#003D1F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Trophy className="h-16 w-16 text-[#00A94F] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Your Learning Journey?
          </h2>
          <p className="text-green-200 mb-8 text-lg">
            Join thousands of professionals advancing their careers with {companyName} Learning Academy.
          </p>
          {!isAuthenticated && (
            <Link 
              to="/enterprise-sso"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#00A94F] hover:bg-[#00B857] text-white rounded-lg font-semibold text-lg transition-colors"
            >
              <Shield className="h-5 w-5" />
              Sign In with Microsoft SSO
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-8 w-auto opacity-75" />
              ) : (
                <div className="h-8 w-8 bg-[#00A94F]/20 rounded flex items-center justify-center">
                  <span className="text-[#00A94F] font-bold">OM</span>
                </div>
              )}
              <span className="text-sm">{companyName} Learning Academy</span>
            </div>
            <div className="text-sm">
              Powered by <span className="text-[#00A94F]">Cerebro Core</span> Learning Platform
            </div>
            <div className="text-sm">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
